import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { cn } from "@/lib/utils/cn";

export interface LinkChangeAction {
  id: string;
  name: string;
  goal: string;
  issue: string;
  is_new: boolean;
}

export interface LinkChangePending {
  id: string;
  proposed_by: string;
  initials: string;
  created_at: string;
  note: string;
  /** Four-eyes: your own proposal is one you may not decide on. */
  is_mine: boolean;
  /** What approval would actually link — ineligible actions already dropped. */
  proposed: LinkChangeAction[];
  added: string[];
  removed: string[];
}

export interface LinkChangeReviewRow {
  resource: { id: string; title: string };
  pending: LinkChangePending;
}

export interface LinkChangeDecision {
  decision: "approve" | "counter";
  request_id: string;
  ids?: string[];
  note?: string;
}

/**
 * The live→proposed diff of a link change, as chips.
 *
 * Shared by the review modal and the edit form so a reviewer sees the same
 * change in both places, and the same one the Django back-office renders.
 */
export function LinkChangeDiff({
  pending,
  ns,
}: {
  pending: LinkChangePending;
  ns: string;
}) {
  const { t } = useTranslation();
  if (pending.added.length === 0 && pending.removed.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        {t(`${ns}.linkChange.noDifference`)}
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {pending.added.map((name) => (
        <span
          key={`add-${name}`}
          className="rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-semibold text-success"
        >
          + {name}
        </span>
      ))}
      {pending.removed.map((name) => (
        <span
          key={`remove-${name}`}
          className="rounded-full bg-destructive/15 px-2 py-0.5 text-[11px] font-semibold text-destructive"
        >
          − {name}
        </span>
      ))}
    </div>
  );
}

/** Who proposed a change, and when — the modal's and the form's shared byline. */
export function LinkChangeByline({
  pending,
  ns,
}: {
  pending: LinkChangePending;
  ns: string;
}) {
  const { t } = useTranslation();
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <span
        aria-hidden
        className="grid size-6 place-items-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground"
      >
        {pending.initials}
      </span>
      {t(`${ns}.linkChange.proposedBy`, { name: pending.proposed_by })}
    </p>
  );
}

/** The explanatory note a counter-proposal carries, when there is one. */
export function LinkChangeNote({
  note,
  ns,
}: {
  note: string;
  ns: string;
}) {
  const { t } = useTranslation();
  if (!note) return null;
  return (
    <p className="rounded-md border border-border bg-muted/40 p-2.5 text-sm italic text-muted-foreground">
      <span className="mr-1.5 text-[10px] font-semibold uppercase not-italic tracking-wide">
        {t(`${ns}.linkChange.note`)}
      </span>
      {note}
    </p>
  );
}

/**
 * The back-office review surface for staged link changes, shared by the tools
 * and financial-aids SPA back-offices.
 *
 * The counterpart of the Django list's review modal, and split the same way:
 * this lists what awaits the reader and approves in bulk, while counter-proposing
 * lives on the edit form, where the action picker is. A row therefore links into
 * its resource rather than trying to negotiate here.
 *
 * Four-eyes is enforced server-side; the reader's own proposals are simply not
 * in the list, so nothing here can be self-approved.
 */
export function LinkChangeReviewModal({
  rows,
  ns,
  isPending,
  onApproveAll,
  onOpenResource,
  onClose,
}: {
  rows: LinkChangeReviewRow[];
  ns: string;
  isPending: boolean;
  onApproveAll: (requestIds: string[]) => void;
  onOpenResource: (resourceId: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  // No error state here: ``onApproveAll`` hands the request to the caller's
  // mutation, which reports its own outcome as a toast. A try/catch around a
  // void call could never see the rejection anyway.
  const approveAll = () => onApproveAll(rows.map((row) => row.pending.id));

  return (
    <Modal
      title={t(`${ns}.linkChange.reviewTitle`)}
      onClose={onClose}
      className="max-w-2xl"
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          {t(`${ns}.linkChange.reviewIntro`)}{" "}
          <strong className="font-semibold text-foreground">
            {t(`${ns}.linkChange.reviewIntroOwn`)}
          </strong>
        </p>

        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li
              key={row.pending.id}
              className="flex flex-col gap-2 rounded-md border border-border bg-card p-3"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="break-words text-sm font-medium">
                  {row.resource.title}
                </p>
                <button
                  type="button"
                  onClick={() => onOpenResource(row.resource.id)}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  {t(`${ns}.linkChange.review`)} <span aria-hidden>→</span>
                </button>
              </div>
              <LinkChangeByline pending={row.pending} ns={ns} />
              <LinkChangeDiff pending={row.pending} ns={ns} />
              <LinkChangeNote note={row.pending.note} ns={ns} />
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
          <span className="mr-auto text-xs text-muted-foreground">
            {t(`${ns}.linkChange.count`, { count: rows.length })}
          </span>
          <Button type="button" variant="outline" onClick={onClose}>
            {t(`${ns}.linkChange.later`)}
          </Button>
          <Button type="button" disabled={isPending} onClick={approveAll}>
            {t(`${ns}.linkChange.approveAll`)}
            <span className="ml-2 rounded-full bg-primary-foreground/20 px-2 py-0.5 text-[11px] font-bold tabular-nums">
              {rows.length}
            </span>
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The list-level banner: how many resources await *your* validation, and the
 * way into the modal. Renders nothing when nothing does.
 */
export function LinkChangeBanner({
  count,
  ns,
  onOpen,
  className,
}: {
  count: number;
  ns: string;
  onOpen: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  if (count === 0) return null;
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-md border border-warning/40 bg-warning/10 p-3",
        className,
      )}
    >
      <p className="text-sm text-foreground">
        {t(`${ns}.linkChange.awaiting`, { count })}
      </p>
      <Button
        type="button"
        variant="outline"
        className="ml-auto"
        onClick={onOpen}
      >
        {t(`${ns}.linkChange.openReview`)}
      </Button>
    </div>
  );
}

/**
 * The note a counter-proposal must carry, and the decision buttons beside it.
 *
 * Lives here rather than in each back-office so the rule that a counter-proposal
 * is refused without a note is stated once, and so the two apps cannot drift on
 * which decisions they offer. Four-eyes: the proposer sees no decision at all —
 * the service would refuse them, and a button that is going to be refused should
 * not be drawn.
 */
export function LinkChangeDecisionControls({
  pending,
  ns,
  countering,
  note,
  onStartCounter,
  onCancelCounter,
  onApprove,
  onCounter,
  busy = false,
}: {
  pending: LinkChangePending;
  ns: string;
  countering: boolean;
  note: string;
  onStartCounter: () => void;
  onCancelCounter: () => void;
  onApprove: () => void;
  onCounter: () => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  if (pending.is_mine) return null;
  if (!countering) {
    return (
      <>
        <Button type="button" variant="outline" onClick={onStartCounter}>
          {t(`${ns}.linkChange.counter`)}
        </Button>
        <Button type="button" disabled={busy} onClick={onApprove}>
          {t(`${ns}.linkChange.approve`)}
        </Button>
      </>
    );
  }
  return (
    <>
      <Button type="button" variant="outline" onClick={onCancelCounter}>
        {t(`${ns}.linkChange.cancelCounter`)}
      </Button>
      <Button
        type="button"
        disabled={busy || note.trim() === ""}
        onClick={onCounter}
      >
        {t(`${ns}.linkChange.submitCounter`)}
      </Button>
    </>
  );
}

/** The explanatory note field, shown while drafting a counter-proposal. */
export function LinkChangeNoteField({
  ns,
  note,
  onNote,
}: {
  ns: string;
  note: string;
  onNote: (next: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">{t(`${ns}.linkChange.noteLabel`)}</span>
      <textarea
        value={note}
        onChange={(e) => onNote(e.target.value)}
        rows={2}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        placeholder={t(`${ns}.linkChange.notePlaceholder`)}
      />
    </label>
  );
}
